
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CompanySettings } from "@/components/settings/companies/CompanySettings";
import { LogoSettings } from "@/components/settings/LogoSettings";
import { PromptsSettings } from "@/components/settings/prompts/PromptsSettings";
import { PlansSettings } from "@/components/settings/plans/PlansSettings";
import { DatabaseDebugView } from "@/components/settings/debug/DatabaseDebugView";
import { AIKeyTester } from "@/components/settings/ai/AIKeyTester";
import { Settings2, MessageSquare, Building2, Image, ScrollText, LayoutDashboard, Cog } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const Settings = () => {
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.type === 'super_admin';
  const isAdmin = currentUser?.type === 'admin';

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Configurações</h2>
          <p className="text-muted-foreground">
            Gerencie as configurações do sistema
          </p>
        </div>
      </div>

      <Tabs defaultValue="logo" className="space-y-4">
        <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10 w-full border-b">
          <div className="container mx-auto">
            <TabsList className="h-14 w-full justify-start rounded-none border-b bg-transparent p-0">
              {/* Logo tab - Todos os usuários podem ver */}
              <TabsTrigger 
                value="logo"
                className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                <Image className="mr-2 h-4 w-4" />
                Logo
              </TabsTrigger>

              {/* Tabs apenas para super_admin */}
              {isSuperAdmin && (
                <>
                  <TabsTrigger 
                    value="company" 
                    className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    Empresas
                  </TabsTrigger>
                  <TabsTrigger 
                    value="prompts"
                    className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Prompts IA
                  </TabsTrigger>
                  <TabsTrigger 
                    value="plans"
                    className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <ScrollText className="mr-2 h-4 w-4" />
                    Planos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="debug"
                    className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Debug
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ai-keys"
                    className="relative h-14 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-3 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <Cog className="mr-2 h-4 w-4" />
                    Teste Chaves IA
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </div>
        </div>

        <div className="container mx-auto">
          {/* Logo content - Todos podem ver */}
          <TabsContent value="logo" className="space-y-4">
            <LogoSettings />
          </TabsContent>

          {/* Contents apenas para super_admin */}
          {isSuperAdmin && (
            <>
              <TabsContent value="company" className="space-y-4">
                <CompanySettings />
              </TabsContent>
              <TabsContent value="prompts" className="space-y-4">
                <PromptsSettings />
              </TabsContent>
              <TabsContent value="plans" className="space-y-4">
                <PlansSettings />
              </TabsContent>
              <TabsContent value="debug" className="space-y-4">
                <DatabaseDebugView />
              </TabsContent>
              <TabsContent value="ai-keys" className="space-y-4">
                <AIKeyTester />
              </TabsContent>
            </>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Settings;
